import debug from 'debug'
import i18next, { i18n } from 'i18next'
import { HttpBackendOptions } from 'i18next-http-backend'
import { getEnv, getPublicEnv } from '@tucan/utils'
import { getBoolean } from 'utils/get.boolean'
import { i18nConfig as i18nConfig } from './i18n.config'

const logger = debug('i18n:init')

export const init = async (
  i18nInstance: i18n,
  config: {
    lng?: string
    ns?: string[]
    preload?: boolean
  } = {},
) => {
  await i18nInstance.init({
    backend: {
      customHeaders: getEnv('TOLGEE_ENDPOINT') ? {
        'X-API-Key': getEnv('TOLGEE_KEY'),
      } : {},
      loadPath: (lang, ns) => {
        logger('loadPath %O', { lang, ns})
        if (getEnv('TOLGEE_ENDPOINT')) {
          return `${getEnv('TOLGEE_ENDPOINT')}/v2/projects/translations/${lang}`
        }

        if (ns.at(0)?.startsWith('company-')) {
          return `/api/i18n/${lang}/company/${ns.at(0)!.replace('company-', '')}`
        }

        return `/api/i18n/${lang}`
      },
      parse(data: string, lang?: string | string[]) {
        const raw = JSON.parse(data)

        if (getEnv('TOLGEE_ENDPOINT')) {
          logger('parse tolgee response %O', { lang })
          return raw[String(lang)]
        }

        logger('parse loaded file')
        return raw
      },
    } as HttpBackendOptions,
    defaultNS: i18nConfig.defaultNS,
    detection: {
      // `en-US` & friends are not routable locales - strip the region so a
      // detected language always matches one of `i18nConfig.locales`
      convertDetectedLanguage: (lng: string) => lng.split('-').shift()!,
      order: [
        'path', 'htmlTag', 'cookie', 'navigator',
      ],
    },
    fallbackLng: i18nConfig.defaultLocale,
    fallbackNS: i18nConfig.defaultNS,
    // without the detector the server has no language at all, so it starts out
    // at the default locale until the language of the route is applied
    lng: config?.lng || (typeof window === 'undefined' ? i18nConfig.defaultLocale : undefined),
    load: 'languageOnly',
    missingKeyHandler: (langs: readonly string[], ns: string, key: string, fallbackValue: string) => {
      if (!getBoolean(getPublicEnv('TOLGEE_ADD_MISSING'))) {
        // only create if called on client side
        return
      }

      if (i18next.language == 'cimode') {
        return
      }

      logger('missing translation key %O', {
        area: ns,
        key,
        languages: langs,
        translation: fallbackValue,
      })

      void langs.map((lang) =>
        fetch('/api/i18n/add', {
          body: JSON.stringify({
            area: ns,
            key,
            lang,
            requireCheck: true,
            translation: fallbackValue,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
        })
          .catch((e) => {
            logger('failed to create missing translation! %O', e)
          }),
      )

    },
    ns: config?.ns || i18nConfig.preloadNs,
    partialBundledLanguages: true,
    preload: config?.preload ? i18nConfig.preload : [],
    saveMissing: true,
    supportedLngs: i18nConfig.locales,
  })
}
